/**
 * nj-binding — the three declarations Stage 1 of the Events-in-New-Journeys
 * build plan calls for. No behavior lands here (stage 2 wires the record,
 * stage 3 builds the Setup views) — this is the schema every later stage
 * builds against, so no control or page type is ever added without an owner.
 */

import { REG, CORE_CFG, ATTACH_FOR, ATTACH_TYPES, docFromEvent, type Doc, type EventLike, type GuestEntry, type QueueConfigLike, type BoardConfigLike } from "./nj-model";
import { requiredEventChecks, type RequiredCheck } from "../events/creation-store";
import { creation, publishEvent, takeEventOffline, getStudioEvent } from "@v0y4ge/api-client";
import {
  updateQueue, setQueueStatus, saveMessageBoard, deleteMessageBoard,
  type QueueStatus, type MessageBoard,
} from "../queues/queues-api";

/** `getStudioEvent` takes the request options every admin read in this app passes
 *  (kit 174e98dd made them required). Same base resolution as the shell. */
const EV_OPTS = { baseUrl: (window as unknown as { __STUDIO_API_BASE__?: string }).__STUDIO_API_BASE__ ?? "" };

/** Re-exported so the screen can import the guest shape from the binding it
 *  already imports the guest writers from. */
export type { GuestEntry } from "./nj-model";

/* ── 1. Field ownership map ──────────────────────────────────────────
 * Every control the designer will show writes to exactly one store, through
 * exactly one op. `scope` is where the control lives in the rail/canvas/panel
 * model; `store` + `op` is who owns the write.
 */
export type FieldScope =
  | "setup.basics" | "setup.details" | "setup.schedule" | "setup.tickets" | "setup.guests" | "setup.messaging"
  /** Queue setup sections (queue editor port, 2026-08-24). */
  | "setup.type" | "setup.hours" | "setup.staffing" | "setup.resources" | "setup.services"
  /** Display-board setup. */
  | "setup.config"
  /** Appointment setup sections (2026-09-08). These three were being USED as
   *  scopes by rows in the table below while the union did not list them, so
   *  every one of those rows was a type error under the kit's strict config and
   *  `ownerOf()` could return a scope no consumer had a case for. */
  | "setup.places" | "setup.window" | "setup.perservice"
  /** Applies across every Setup section, not one of them. */
  | "setup.*"
  | "publish" | "page" | "block";
export type FieldStore = "record" | "doc" | "derived";

export interface FieldOwnership {
  /** Human-readable label for the map. NEVER matched against — see `keys`. */
  field: string;
  /** The real record/document field names this row owns. `ownerOf()` matches
   *  these, so a row with a compound or annotated label stays reachable. */
  keys: string[];
  scope: FieldScope;
  store: FieldStore;
  op: string;
  note?: string;
}

/** MIRROR WARNING: this array and the ownership table in
 *  "Events in New Journeys - Build Plan.dc.html" are two copies of the SAME
 *  declaration. They have drifted before. Change both, or neither. */
export const EVENT_FIELD_OWNERSHIP: FieldOwnership[] = [
  { field: "title", keys: ["title"], scope: "setup.basics", store: "record", op: "updateEvent" },
  { field: "description", keys: ["description"], scope: "setup.basics", store: "record", op: "updateEvent" },
  { field: "tags", keys: ["tags"], scope: "setup.basics", store: "record", op: "updateEvent" },
  { field: "heroImage", keys: ["heroImage"], scope: "setup.basics", store: "record", op: "updateEvent", note: "URL field or an image dropped on the card preview" },
  { field: "method (format)", keys: ["method"], scope: "setup.details", store: "record", op: "updateEvent" },
  { field: "visibility", keys: ["visibility"], scope: "setup.details", store: "record", op: "updateEvent" },
  { field: "location / venue / address", keys: ["location", "venueName", "address", "locationId", "locationMode"], scope: "setup.details", store: "record", op: "updateEvent" },
  { field: "virtual provider / join link", keys: ["virtualProvider"], scope: "setup.details", store: "record", op: "updateEvent" },
  { field: "registration window", keys: ["registrationOpens", "registrationCloses"], scope: "setup.details", store: "record", op: "updateEventRegistration", note: "MOVED here from the register page; the single registrationWindow key is deleted" },
  { field: "consent and marketing, terms copy", keys: ["consent", "termsCopy"], scope: "setup.details", store: "record", op: "updateEventRegistration", note: "MOVED here from the register page" },
  { field: "after-registration redirect", keys: ["redirectUrl"], scope: "setup.details", store: "record", op: "updateEventRegistration", note: "MOVED here from the register page" },
  { field: "start / end / timezone / all-day", keys: ["start", "end", "timezone", "allDay"], scope: "setup.schedule", store: "record", op: "updateEvent" },
  { field: "recurrence", keys: ["repeatIndex"], scope: "setup.schedule", store: "record", op: "updateEvent" },
  { field: "custom recurrence dates", keys: ["customDates"], scope: "setup.schedule", store: "record", op: "updateEvent", note: "only when repeatIndex is the Custom entry" },
  { field: "per-day hours", keys: ["dayHours"], scope: "setup.schedule", store: "record", op: "updateEvent", note: "multi-day events only" },
  { field: "selected ticket types", keys: ["ticketTypeIds"], scope: "setup.tickets", store: "record", op: "updateEventTickets", note: "selected from the library, never created here" },
  { field: "per-type allocation", keys: ["ticketTypeConfigs"], scope: "setup.tickets", store: "record", op: "updateEventTickets" },
  { field: "total tickets available", keys: ["capacity"], scope: "setup.tickets", store: "record", op: "updateEventTickets", note: "entered directly, then allocated across types; partial allocation is legal" },
  { field: "waitlist", keys: ["waitlist"], scope: "setup.tickets", store: "record", op: "updateEventTickets", note: "moved off the register page, stage 0" },
  { field: "selected ticket groups", keys: ["ticketGroupIds"], scope: "setup.tickets", store: "record", op: "attachEventTicketGroups", note: "doc attach.ticketGroupId is a MIRROR of the first selection, written by the same writers" },
  { field: "guest list / segments", keys: ["guestList"], scope: "setup.guests", store: "record", op: "updateEventGuestList | importEventGuests" },
  { field: "message flow selection", keys: ["messagingFlowId"], scope: "setup.messaging", store: "record", op: "updateEventMessaging", note: "selects which library flow runs; flow authoring itself is the Flow view" },
  { field: "registration fields that stayed page-scope", keys: ["approvalMode", "maxGuests", "standardFields", "promoId"], scope: "page", store: "record", op: "updateEventRegistration" },
  { field: "question group reference", keys: ["attach.groupId"], scope: "page", store: "doc", op: "putJourneyDocument" },
  { field: "survey reference", keys: ["surveys"], scope: "setup.messaging", store: "record", op: "updateEventMessaging", note: "moved off the journey document 2026-09-02 — an event's survey is the event's, so two journeys against one event cannot disagree about it" },
  { field: "coupons accepted", keys: ["coupons"], scope: "setup.tickets", store: "record", op: "updateEventTickets", note: "event-scoped, flat (not per method) — a coupon discounts a price, and an event has one way in" },
  { field: "promo reference", keys: ["attach.promoId"], scope: "page", store: "doc", op: "putJourneyDocument" },
  { field: "page text", keys: ["pages[].h", "pages[].s"], scope: "page", store: "doc", op: "putJourneyDocument" },
  { field: "core settings", keys: ["pages[].toggles"], scope: "page", store: "doc", op: "putJourneyDocument" },
  { field: "blocks", keys: ["pages[].content"], scope: "block", store: "doc", op: "putJourneyDocument" },
  { field: "flow graph", keys: ["flow"], scope: "page", store: "doc", op: "putJourneyDocument | saveBuilderEventFlow", note: "one tree since 2026-08-31 — replaced flowMain/flowYes/flowNo" },
  { field: "entry page", keys: ["entryUid"], scope: "publish", store: "doc", op: "putJourneyDocument", note: "journey-level, stage 1. NOT surfaced in any Setup section yet" },
  { field: "publishing settings", keys: ["publish.slug", "publish.tenantHost", "publish.seoTitle", "publish.shareImage"], scope: "publish", store: "doc", op: "putJourneyDocument", note: "NOT surfaced in any Setup section yet" },
  { field: "appearance", keys: ["publish.theme", "publish.font", "publish.customCss"], scope: "publish", store: "doc", op: "putJourneyDocument", note: "NOT surfaced in any Setup section yet" },
  { field: "published page bundle", keys: ["publishedBundle"], scope: "publish", store: "derived", op: "saveEventLandingDraft | publishEvent", note: "not user-editable. publishEvent POSTs /v1/landing/{id}/publish, so publishing does hit the landing surface; what is still missing is the document → landing-envelope draft write (saveEventLandingDraft). publishEventLanding does not exist yet — Claude Code confirmed 2026-08-22 they will add it" },
  { field: "ANY setup field, before an owning event exists", keys: ["setup"], scope: "setup.*", store: "doc", op: "putJourneyDocument setup{} (debounced)", note: "holding store: a template-created (local:) journey has no record to write to, and no attach step exists, so values stay in Doc.setup" },
];

/** Enforce "no control without an owner" — matches the row's real field
 *  `keys`, never its display label, so annotated rows stay reachable. */
export function ownerOf(field: string): FieldOwnership | undefined {
  return EVENT_FIELD_OWNERSHIP.find((f) => f.keys.includes(field));
}

/* ── 2. Inspector schema per page type ────────────────────────────────
 * Composed from what the registry already declares (REG's core + toggles,
 * CORE_CFG, ATTACH_FOR) rather than duplicated — this is the formal shape the
 * panel renders from, so a page type with no declaration shows nothing extra.
 */
export interface PageInspectorSchema {
  pageType: string;
  hasPageText: true;
  hasCore: boolean;
  coreSettings: string[];       // keys from CORE_CFG[pageType]
  attachment: { type: string; libraryTitle: string; route: string } | null;
  blocksAllowed: "all" | "all-minus-hero";
}

export function inspectorSchemaFor(pageType: string): PageInspectorSchema {
  const reg = REG.find((r) => r.id === pageType);
  const attachType = ATTACH_FOR[pageType];
  const attach = attachType
    ? { type: attachType, libraryTitle: ATTACH_TYPES[attachType] ?? attachType, route: attachType }
    : null;
  return {
    pageType,
    hasPageText: true,
    hasCore: !!reg?.core,
    coreSettings: (CORE_CFG[pageType] ?? []).map((row) => row[0]),
    attachment: attach,
    blocksAllowed: reg?.core ? "all-minus-hero" : "all",
  };
}

/* ── 3. Object binding per journey type ───────────────────────────────
 * What a journey of this type writes to, besides the document. Queues have
 * had this implicitly since the write-back landed; naming it here is what
 * makes appointments and experiences a configuration instead of a rebuild.
 */
export interface JourneyBinding {
  type: string;
  /** false = no owning object; the document is the only store (experiences). */
  ownsRecord: boolean;
  ops?: {
    read: string;
    update: string;
    publish?: string;
    takeOffline?: string;
    cancel?: string;
    discard?: string;
  };
  /** Name of the nj-model function that derives a Doc from the real object. */
  deriveDocFn?: string;
  /** Name of the function (screens/events/creation-store.ts today) that returns
   *  the readiness rows the Publish view and the rail's dots render from. */
  requiredChecksFn?: string;
}

export const EVENT_BINDING: JourneyBinding = {
  type: "Event",
  ownsRecord: true,
  ops: {
    read: "getStudioEvent",
    update: "creation.updateEvent",
    publish: "publishEvent",
    takeOffline: "takeEventOffline",
    cancel: "creation.cancelEvent",
    discard: "creation.discardEvent",
  },
  deriveDocFn: "docFromEvent",
  requiredChecksFn: "requiredEventChecks",
};

export const QUEUE_BINDING: JourneyBinding = {
  type: "Queue",
  ownsRecord: true,
  ops: { read: "listQueues", update: "updateQueue", publish: "setQueueStatus(open)", takeOffline: "setQueueStatus(closed)" },
  deriveDocFn: "docFromQueue",
  requiredChecksFn: "queueReadiness",
};

export const BOARD_BINDING: JourneyBinding = {
  type: "Board",
  ownsRecord: true,
  ops: { read: "listMessageBoards", update: "saveMessageBoard", discard: "deleteMessageBoard" },
  deriveDocFn: "docFromBoard",
  requiredChecksFn: "boardReadiness",
};

/* ── Queue field ownership — the queue editor's every control, ported ──
 * Same contract as the event map: one control, one store, one op. The queue
 * editor wrote all of these through updateQueue (autosaved) and published
 * through setQueueStatus; moving the surface into the designer does not move
 * the writes. */
export const QUEUE_FIELD_OWNERSHIP: FieldOwnership[] = [
  { field: "queue name", keys: ["name"], scope: "setup.type", store: "record", op: "updateQueue" },
  { field: "queue type", keys: ["typeId"], scope: "setup.type", store: "record", op: "updateQueue", note: "picking a type also seeds the journey and the type's wait-target preset" },
  { field: "location", keys: ["locationId", "locationName"], scope: "setup.type", store: "record", op: "createQueue", note: "set at create; a queue is owned by its location's branch company, so it can't move afterwards" },
  { field: "ticket prefix", keys: ["ticketPrefix"], scope: "setup.type", store: "record", op: "updateQueue" },
  { field: "wait target", keys: ["slaTargetMins"], scope: "setup.type", store: "record", op: "updateQueue" },
  { field: "hero image", keys: ["heroImage"], scope: "setup.type", store: "record", op: "updateQueue", note: "tops every page of the queue journey" },
  { field: "journey seed source", keys: ["journeySource"], scope: "setup.type", store: "record", op: "updateQueue" },
  { field: "opening hours", keys: ["hours"], scope: "setup.hours", store: "record", op: "updateQueue" },
  { field: "assigned staff", keys: ["assignedStaff"], scope: "setup.staffing", store: "record", op: "updateQueue" },
  { field: "positions needed", keys: ["needed"], scope: "setup.staffing", store: "record", op: "updateQueue" },
  { field: "resources", keys: ["resources"], scope: "setup.resources", store: "record", op: "updateQueue" },
  { field: "ordered service selection", keys: ["serviceSelection", "services"], scope: "setup.services", store: "record", op: "updateQueue", note: "categories resolve to their catalogue services live; `services` is the resolved flat list" },
  { field: "message flow selection", keys: ["messagingFlowId"], scope: "setup.messaging", store: "record", op: "updateQueue" },
  { field: "open / paused / closed", keys: ["status"], scope: "publish", store: "record", op: "setQueueStatus" },
  { field: "pages, blocks and flow", keys: ["pages", "flow"], scope: "page", store: "doc", op: "putJourneyDocument" },
  { field: "ANY queue setup field, before a queue record exists", keys: ["qsetup"], scope: "setup.*", store: "doc", op: "putJourneyDocument qsetup{} (debounced)" },
];

/* ── Display-board field ownership ─────────────────────────────────── */
export const BOARD_FIELD_OWNERSHIP: FieldOwnership[] = [
  { field: "board name", keys: ["name"], scope: "setup.config", store: "record", op: "saveMessageBoard" },
  { field: "queues shown", keys: ["queueIds"], scope: "setup.config", store: "record", op: "saveMessageBoard" },
  { field: "theme", keys: ["theme"], scope: "setup.config", store: "record", op: "saveMessageBoard" },
  { field: "queuer display options", keys: ["nameFormat", "showWaitEstimates"], scope: "setup.config", store: "record", op: "saveMessageBoard" },
  { field: "welcome message", keys: ["message"], scope: "setup.config", store: "record", op: "saveMessageBoard" },
  { field: "fixed content blocks", keys: ["blocks", "layout"], scope: "setup.config", store: "record", op: "saveMessageBoard", note: "layout stays derived from the blocks for back-compat consumers" },
  { field: "board configuration, before the board is saved", keys: ["boardCfg"], scope: "setup.*", store: "doc", op: "putJourneyDocument boardCfg{} (debounced)" },
];


/* ── Appointment field ownership (2026-08-31) ────────────────────────
 * The type with no record. Every row is either the DOCUMENT (the funnel's own
 * presentation and selection) or the CATALOGUE (tenant-wide truth, referenced
 * and never copied). Nothing here writes an appointment record, because none
 * exists — that is the ask in
 * CD-API-REQUEST-service-scoped-attachments-2026-08-31.md §8. */
export const APPOINTMENT_FIELD_OWNERSHIP: FieldOwnership[] = [
  { field: "ordered service selection", keys: ["apptSetup.serviceSelection"], scope: "setup.services", store: "doc", op: "putJourneyDocument" },
  { field: "group services by category", keys: ["apptSetup.groupByCategory"], scope: "setup.services", store: "doc", op: "putJourneyDocument" },
  { field: "service → line-of-business membership", keys: ["service.lob"], scope: "setup.services", store: "record", op: "updateCatalogueService", note: "TENANT-WIDE: moving a service changes every surface that reads the catalogue" },
  { field: "duration, buffers, price", keys: ["service.durationMin", "service.preMin", "service.postMin"], scope: "setup.services", store: "record", op: "read-only · updateCatalogueService", note: "referenced with a jump; never edited from inside one funnel" },
  { field: "locations offered", keys: ["apptSetup.locationIds"], scope: "setup.places", store: "doc", op: "putJourneyDocument" },
  { field: "methods exposed", keys: ["apptSetup.methods"], scope: "setup.places", store: "doc", op: "putJourneyDocument", note: "ceilinged by each service's own channels" },
  { field: "booking + cancellation windows", keys: ["service.minAdvance", "service.maxAdvance", "service.minCancel", "service.disableBefore", "service.disableAfter"], scope: "setup.window", store: "record", op: "read-only · updateCatalogueService" },
  { field: "booking questions per service", keys: ["service.questionGroups.appointments"], scope: "setup.perservice", store: "record", op: "read-only · updateCatalogueService" },
  { field: "survey / promo / flow per service", keys: ["service.surveys", "service.promos", "service.flows"], scope: "setup.perservice", store: "record", op: "BLOCKED — CD-API-REQUEST-service-scoped-attachments-2026-08-31" },
  { field: "standard fields + consent + terms", keys: ["apptSetup.standardFields", "apptSetup.consent", "apptSetup.termsCopy", "apptSetup.policyUrl"], scope: "setup.details", store: "doc", op: "putJourneyDocument" },
  { field: "post-booking redirect", keys: ["apptSetup.redirectUrl"], scope: "setup.details", store: "doc", op: "putJourneyDocument" },
  { field: "fallback message flow", keys: ["apptSetup.messagingFlowId"], scope: "setup.messaging", store: "doc", op: "putJourneyDocument" },
  { field: "legacy journey-wide survey / promo", keys: ["apptSetup.legacySurveyId", "apptSetup.legacyPromoId"], scope: "setup.perservice", store: "doc", op: "putJourneyDocument (remove only)", note: "read-only rows; settable only to null, and gone for good once removed" },
  { field: "publishing settings", keys: ["publish.slug", "publish.tenantHost", "publish.seoTitle", "publish.shareImage"], scope: "publish", store: "doc", op: "putJourneyDocument" },
  { field: "pages, blocks and flow", keys: ["pages", "flow"], scope: "page", store: "doc", op: "putJourneyDocument" },
];

export const APPOINTMENT_BINDING: JourneyBinding = {
  type: "Appointment",
  ownsRecord: false, // no journey-bearing record exists for a LOB funnel — confirmed correct by Claude Code 2026-08-22; blocked on the availability contract (CD-API-REQUEST-journey-availability-2026-08-14)
  deriveDocFn: "docFromApptGroup",
};

export const EXPERIENCE_BINDING: JourneyBinding = {
  type: "Experience",
  ownsRecord: false, // by design — an anchor journey plus attached journeys by reference
};

export const JOURNEY_BINDINGS: Record<string, JourneyBinding> = {
  Queue: QUEUE_BINDING,
  Event: EVENT_BINDING,
  Appointment: APPOINTMENT_BINDING,
  Experience: EXPERIENCE_BINDING,
  Board: BOARD_BINDING,
};

export function bindingFor(doc: Doc): JourneyBinding | undefined {
  return doc.type ? JOURNEY_BINDINGS[doc.type] : undefined;
}

/* ── Stage 2 — read + readiness ───────────────────────────────────────
 * The record half of an `event:` journey: fetch the real event, derive its
 * document, and compute the SAME readiness rows the wizard's Publish step
 * gates on — one rule set, read from requiredEventChecks() rather than
 * re-implemented here, so the rail's dots and the designer's Publish view
 * can never drift from what actually blocks a real publish.
 */

/** requiredEventChecks() reads a wizard-shaped config; EventLike carries the
 *  same field names once fetched live, so this is a rename, not a remap. */
function eventLikeToChecksInput(e: EventLike): Record<string, unknown> {
  return {
    title: e.title, description: e.description, method: e.method, visibility: e.visibility,
    location: e.location, locationId: e.locationId, venueName: e.venueName, address: e.address,
    virtualProvider: e.virtualProvider, start: e.start, end: e.end,
    ticketTypeIds: e.ticketTypeIds, ticketGroupIds: e.ticketGroupIds, tickets: e.tickets,
  };
}

export function eventReadiness(e: EventLike, opts?: { template?: boolean }): RequiredCheck[] {
  return requiredEventChecks(eventLikeToChecksInput(e), opts);
}

/** Raw StudioEventDto → EventLike. Field names not yet visible on a derived-
 *  list row (format, locationName/locationAddress/locationId, structured
 *  `location`) are read with the same camelCase-then-snake_case tolerance
 *  `CreateEventPublish.tsx`'s loadEvent hydration already uses for this exact
 *  DTO — not a new assumption, the established one, kept in one place. */
function rawToEventLike(id: string, raw: Record<string, unknown>): EventLike {
  const s = (v: unknown): string | undefined => (v == null ? undefined : String(v));
  const format = s(raw.format);
  const method = format === "hybrid" ? { inPerson: true, virtual: true }
    : format === "virtual" ? { inPerson: false, virtual: true }
    : format === "in_person" ? { inPerson: true, virtual: false }
    : undefined;
  const venueName = s(raw.locationName ?? (raw as Record<string, unknown>).location_name);
  const address = s(raw.locationAddress ?? (raw as Record<string, unknown>).location_address);
  // raw.location is the STRUCTURED validated-address object on a real DTO
  // (store.ts's `loadEvent` hydration), never a display string — never
  // stringify it directly.
  const locationDisplay = venueName || address || (typeof raw.location === "string" ? raw.location : undefined);
  return {
    id, title: s(raw.title) ?? "", status: s(raw.status) ?? "draft",
    description: s(raw.description), location: locationDisplay,
    venueName, address,
    locationId: s(raw.locationId ?? (raw as Record<string, unknown>).location_id),
    method, visibility: s(raw.visibility), virtualProvider: s(raw.virtualProvider),
    start: s(raw.start), end: s(raw.end),
    ticketTypeIds: (raw.ticketTypeIds as string[] | undefined) ?? undefined,
    ticketGroupIds: (raw.ticketGroupIds as string[] | undefined) ?? undefined,
    tickets: (raw.tickets as unknown[] | undefined) ?? undefined,
    isPaid: !!raw.isPaid, capacity: (raw.capacity as number | null | undefined) ?? null,
    bookingQuestions: (raw.bookingQuestions as EventLike["bookingQuestions"]) ?? undefined,
    surveys: (raw.surveys as string[] | undefined) ?? undefined,
  };
}

/** Fetches the real event, derives its document, and returns readiness
 *  alongside it. `getStudioEvent` is the same read op the wizard's edit-open
 *  hydration uses — no new contract, per the stage-2 risk check. */
export async function loadEventDoc(eventId: string): Promise<{ key: string; doc: Doc; readiness: RequiredCheck[]; record: EventLike } | null> {
  const raw = await getStudioEvent(eventId, EV_OPTS) as unknown as Record<string, unknown>;
  if (!raw) return null;
  const like = rawToEventLike(eventId, raw);
  const { key, doc } = docFromEvent(like);
  return { key, doc, readiness: eventReadiness(like), record: like };
}

/* ── Stage 3 — Setup writes ───────────────────────────────────────────
 * One function per setup.* scope in EVENT_FIELD_OWNERSHIP, each calling the
 * op that scope's rows name. Guests has two: an add/import writes through
 * updateEventGuestList, so both funnel through the same call here.
 */
export async function patchEventBasics(id: string, patch: Record<string, unknown>): Promise<void> {
  await creation.updateEvent(id, patch as unknown as Parameters<typeof creation.updateEvent>[1]);
}
export async function patchEventDetails(id: string, patch: Record<string, unknown>): Promise<void> {
  await creation.updateEvent(id, patch as unknown as Parameters<typeof creation.updateEvent>[1]);
}
export async function patchEventSchedule(id: string, patch: Record<string, unknown>): Promise<void> {
  await creation.updateEvent(id, patch as unknown as Parameters<typeof creation.updateEvent>[1]);
}
export async function patchEventTickets(id: string, patch: Record<string, unknown>): Promise<void> {
  await creation.updateEventTickets(id, patch as unknown as Parameters<typeof creation.updateEventTickets>[1]);
}
/** Ticket GROUPS attach through their own op per the ownership map — groups are
 *  library objects referenced by id, never copied onto the event. */
export async function attachEventTicketGroupIds(id: string, groupIds: string[]): Promise<void> {
  await creation.attachEventTicketGroups(id, { ticketGroupIds: groupIds } as unknown as Parameters<typeof creation.attachEventTicketGroups>[1]);
}
export async function patchEventRegistration(id: string, patch: Record<string, unknown>): Promise<void> {
  await creation.updateEventRegistration(id, patch as unknown as Parameters<typeof creation.updateEventRegistration>[1]);
}
export async function patchEventGuestList(id: string, guests: GuestEntry[]): Promise<void> {
  await creation.updateEventGuestList(id, { guestList: guests } as unknown as Parameters<typeof creation.updateEventGuestList>[1]);
}

export async function patchEventMessaging(id: string, patch: Record<string, unknown>): Promise<void> {
  await creation.updateEventMessaging(id, patch as unknown as Parameters<typeof creation.updateEventMessaging>[1]);
}

/* ── Stage 5 — publish, offline, cancel ───────────────────────────────
 * CORRECTED 2026-08-22 (Claude Code, answering the newjourneys API request):
 * publishing is a first-class operation, not a status write. `publishEvent`
 * and `takeEventOffline` are FLAT exports of @v0y4ge/api-client (not on the
 * `creation` namespace) and hit their own routes — POST /v1/landing/{id}/publish
 * and POST /v1/admin/events/{id}/take-offline. The earlier reading of
 * CreateEventPublish.tsx's `status: "live"` payload was the wrong path: a
 * status write diverges from what the backend actually does on publish.
 * Cancel and discard remain distinct real ops on the namespace.
 */
export async function publishEventRecord(id: string): Promise<void> {
  await publishEvent(id);
}
export async function takeEventRecordOffline(id: string): Promise<void> {
  await takeEventOffline(id);
}
export async function cancelEventRecord(id: string): Promise<void> {
  await creation.cancelEvent(id as unknown as Parameters<typeof creation.cancelEvent>[0]);
}

/* ── Queue setup writes (queue editor port, 2026-08-24) ───────────────
 * The queue editor autosaved every field through updateQueue and published
 * through setQueueStatus. The designer's Setup views call these, so the two
 * surfaces cannot drift: same ops, same payload shape.
 */
export async function patchQueueConfig(id: string, patch: Partial<QueueConfigLike>): Promise<void> {
  await updateQueue(id, patch as unknown as Parameters<typeof updateQueue>[1]);
}
export async function setQueueLifecycle(id: string, status: QueueStatus): Promise<void> {
  await setQueueStatus(id, status);
}

/** One readiness row set for a queue — the wizard's Review-step checklist,
 *  verbatim: name, location, a seeded journey, ≥1 opening-hours set, ≥1
 *  service. `tab` is the Setup view that fills the row. */
export interface SetupCheck { label: string; done: boolean; tab: string }
export function queueReadiness(q: Partial<QueueConfigLike>, _pageCount: number): SetupCheck[] {
  const hours = q.hours ?? [];
  return [
    /* Tab renamed `qtype` → `qsetup` (operator, 2026-09-02): the page is Setup
       now, because the starter picker it was named after is gone. The queue's
       pages are seeded on creation, so there is nothing on that page to choose.

       "Journey — pages seeded" retired with it. It asserted `pageCount > 0` on a
       journey whose pages are created by the template — a check that could only
       fail if the seed itself was broken, and one an author could not act on. The
       `_pageCount` argument stays so every caller does not need editing; it is
       unread on purpose. */
    { label: "Queue name", done: !!(q.name ?? "").trim(), tab: "qsetup" },
    { label: "Location", done: !!q.locationId, tab: "qsetup" },
    { label: "Opening hours — at least one set", done: hours.length > 0 && hours.every((h) => h.days.length > 0), tab: "qhours" },
    { label: "Services — at least one", done: (q.services ?? []).length > 0, tab: "qservices" },
  ];
}

/* ── Display-board writes ─────────────────────────────────────────────
 * `layout` stays derived from the blocks, exactly as the retired
 * QueueBoardBuilder derived it, so legacy consumers (list column, public
 * renderer) are unaffected by boards authored in the designer.
 */
export async function saveBoardConfig(cfg: BoardConfigLike): Promise<void> {
  const layout: MessageBoard["layout"] = cfg.blocks.some((b) => b.type === "up-next") ? "full-list" : "now-serving";
  await saveMessageBoard({
    id: cfg.id, name: cfg.name.trim() || "Untitled board", queueIds: cfg.queueIds,
    locationId: cfg.locationId ?? null, layout, theme: cfg.theme,
    showWaitEstimates: cfg.showWaitEstimates, nameFormat: cfg.nameFormat,
    message: cfg.message, blocks: cfg.blocks,
  });
}
export async function deleteBoardConfig(id: string): Promise<void> {
  await deleteMessageBoard(id);
}
export function boardReadiness(b: Partial<BoardConfigLike>): SetupCheck[] {
  return [
    { label: "Board name", done: !!(b.name ?? "").trim(), tab: "boardcfg" },
    { label: "Queues shown — at least one", done: (b.queueIds ?? []).length > 0, tab: "boardcfg" },
    { label: "Content — at least one block", done: (b.blocks ?? []).length > 0, tab: "boardcfg" },
  ];
}
